import React, { Component } from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import moment from 'moment';
import TimesheetReport from './TimesheetReport';
import ComplianceReport from './ComplianceReport';
import ExceptionReport from './ExceptionReport';
import { downloadTimeExport } from '../../../services/timeTrackingService';
import { isAllLocations } from '../../../util/locationUtils';
// Constants to match calendar/financials styling
const DESKTOP_BREAKPOINT = 1024;

/**
 * TimeReports - Main reporting page for time tracking data
 *
 * Props:
 * - userType: string - 'installer' or 'dealer'
 * - typeId: number - installer location ID
 */
class TimeReports extends Component {
  state = {
    activeTab: 'timesheet',
    // Date range state - matching Financials pattern
    range: 'month',
    selectedDate: moment().startOf('month'),
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
    today: moment(),
    // Custom date range
    customStartDate: '',
    customEndDate: '',
    showCustomInputs: false,
    // Other state
    selectedTechnician: null,
    technicians: [],
    exporting: false,
    isMobile: window.innerWidth < DESKTOP_BREAKPOINT,
    // Time tracking enabled status
    timeTrackingEnabled: false
  };

  // Check if time tracking is enabled for this location
  checkTimeTrackingEnabled = () => {
    try {
      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
      const timeTrackingMode = companyData.time_tracking_mode || 'none';
      return timeTrackingMode !== 'none';
    } catch (error) {
      console.error('Error checking time tracking status:', error);
      return false;
    }
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    // Check if time tracking is enabled
    const timeTrackingEnabled = this.checkTimeTrackingEnabled();
    this.setState({ timeTrackingEnabled });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const isMobile = window.innerWidth < DESKTOP_BREAKPOINT;
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile });
    }
  };

  // Get display string for date range - matches Financials
  getDisplayString = (date, range) => {
    let dateString = '';
    if (range === 'day') {
      dateString = date.format('ddd, MMM Do YYYY');
    } else if (range === 'week') {
      const startOfWeek = moment(date).startOf('week');
      const endOfWeek = moment(date).endOf('week');
      dateString = startOfWeek.format('MMM Do') + ' - ' + endOfWeek.format('MMM Do, YYYY');
    } else if (range === 'month') {
      const startOfMonth = moment(date).startOf('month');
      const endOfMonth = moment(date).endOf('month');
      dateString = startOfMonth.format('MMM Do') + ' - ' + endOfMonth.format('MMM Do, YYYY');
    } else if (range === 'custom') {
      const startDate = this.state.startDate || date;
      const endDate = this.state.endDate || date;
      dateString = startDate.format('MMM Do, YYYY') + ' - ' + endDate.format('MMM Do, YYYY');
    } else {
      dateString = date.format('ddd, MMM Do YYYY');
    }
    return dateString;
  };

  // Handle range change (Day/Week/Month/Custom) - matches Financials
  handleRange = (event) => {
    const { value } = event.currentTarget;
    let newDay = moment(this.state.selectedDate);
    let endDate = '';

    if (value === 'custom') {
      const defaultStart = this.state.startDate ? this.state.startDate.format('YYYY-MM-DD') : moment().startOf('month').format('YYYY-MM-DD');
      const defaultEnd = this.state.endDate ? this.state.endDate.format('YYYY-MM-DD') : moment().endOf('month').format('YYYY-MM-DD');

      this.setState({
        range: 'custom',
        showCustomInputs: true,
        customStartDate: defaultStart,
        customEndDate: defaultEnd
      });
      return;
    } else {
      this.setState({ showCustomInputs: false });
    }

    if (value === 'week') {
      newDay = moment(newDay).startOf('week');
      endDate = moment(newDay).endOf('week');
    }
    if (value === 'month') {
      newDay = moment(newDay).startOf('month');
      endDate = moment(newDay).endOf('month');
    }
    if (value === 'day') {
      endDate = moment(newDay).endOf('day');
    }

    this.setState({ range: value, selectedDate: newDay, startDate: newDay, endDate });
  };

  // Handle display range navigation (arrows) - matches Financials
  handleDisplayRange = (event) => {
    const { value } = event.currentTarget;
    let newDay = moment(this.state.selectedDate);
    let endDate;

    if (this.state.range === 'day') {
      if (value === 'today') {
        newDay = moment();
      }
      if (value === 'less') {
        newDay = moment(newDay).subtract(1, 'day');
      }
      if (value === 'more') {
        newDay = moment(newDay).add(1, 'day');
      }
      endDate = moment(newDay).endOf('day');
    }

    if (this.state.range === 'week') {
      if (value === 'today') {
        newDay = moment().startOf('week');
      }
      if (value === 'less') {
        newDay = moment(newDay).subtract(1, 'week');
      }
      if (value === 'more') {
        newDay = moment(newDay).add(1, 'week');
      }
      endDate = moment(newDay).endOf('week');
    }

    if (this.state.range === 'month') {
      if (value === 'today') {
        newDay = moment().startOf('month');
      }
      if (value === 'less') {
        newDay = moment(newDay).subtract(1, 'month');
      }
      if (value === 'more') {
        newDay = moment(newDay).add(1, 'month');
      }
      endDate = moment(newDay).endOf('month');
    }

    this.setState({ selectedDate: newDay, startDate: newDay, endDate });
  };

  // Custom date range handlers - matches Financials
  handleCustomDateRange = () => {
    const { customStartDate, customEndDate } = this.state;

    if (!customStartDate || !customEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    const startDate = moment(customStartDate).startOf('day');
    const endDate = moment(customEndDate).endOf('day');

    if (endDate.isBefore(startDate)) {
      alert('End date must be after start date');
      return;
    }

    this.setState({
      range: 'custom',
      selectedDate: startDate,
      startDate: startDate,
      endDate: endDate
    });
  };

  handleCustomStartDateChange = (event) => {
    this.setState({ customStartDate: event.target.value });
  };

  handleCustomEndDateChange = (event) => {
    this.setState({ customEndDate: event.target.value });
  };

  // Handle technician filter change
  handleTechnicianChange = (technicianId) => {
    this.setState({
      selectedTechnician: technicianId ? parseInt(technicianId, 10) : null
    });
  };

  // Update technicians list from child component
  setTechnicians = (technicians) => {
    this.setState({ technicians });
  };

  // Handle export
  handleExport = async () => {
    const { typeId } = this.props;
    const { startDate, endDate, selectedTechnician, activeTab } = this.state;
    const locationId = isAllLocations(this.props.selectedLocationId)
      ? (this.props.locations?.[0]?.location_id || typeId)
      : (this.props.selectedLocationId || typeId);

    this.setState({ exporting: true });

    try {
      await downloadTimeExport(locationId, {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        technicianId: selectedTechnician,
        status: activeTab === 'exceptions' ? 'flagged,auto_closed' : undefined
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to download CSV. Please try again.');
    } finally {
      this.setState({ exporting: false });
    }
  };

  render() {
    const { typeId } = this.props;
    const {
      activeTab,
      range,
      selectedDate,
      startDate,
      endDate,
      today,
      customStartDate,
      customEndDate,
      showCustomInputs,
      selectedTechnician,
      technicians,
      exporting,
      isMobile,
      timeTrackingEnabled
    } = this.state;
    const effectiveLocationId = isAllLocations(this.props.selectedLocationId)
      ? 'all'
      : (this.props.selectedLocationId || typeId);

    // Pass dates as strings to child components
    const dateRange = {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };
    const containerClass = isMobile ? 'time-reports-container mobile' : 'time-reports-container';

    // Show setup instructions if time tracking is not enabled
    if (!timeTrackingEnabled) {
      return (
        <div className={containerClass}>
          <div className="time-tracking-disabled-notice">
            <div className="notice-icon">
              <i className="fa-sharp fa-regular fa-clock fa-3x text-muted"></i>
            </div>
            <h4>Time Tracking Not Enabled</h4>
            <p className="text-muted mb-4">
              Time tracking is currently disabled for this location. To start recording staff check-in/check-out times,
              you can enable time tracking in the <strong>Profile</strong> section under <strong>Location Settings</strong>.
            </p>
            <a href="/profile" className="btn btn-primary">
              <i className="fa-sharp fa-regular fa-cog mr-2"></i>
              Go to Profile Settings
            </a>
          </div>

          <style>{`
            .time-tracking-disabled-notice {
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 48px 32px;
              text-align: center;
              max-width: 500px;
              margin: 40px auto;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }

            .time-tracking-disabled-notice .notice-icon {
              margin-bottom: 24px;
            }

            .time-tracking-disabled-notice h4 {
              color: #374151;
              margin-bottom: 16px;
            }

            .time-tracking-disabled-notice p {
              line-height: 1.6;
            }

            .time-reports-container {
              padding: 24px;
              max-width: 1400px;
              margin: 0 auto;
              background: transparent;
              min-height: 100vh;
            }

            .time-reports-container.mobile {
              padding: 16px;
            }
          `}</style>
        </div>
      );
    }

    return (
      <div className={containerClass}>
        {/* Header Section */}
        <div className="header-section">
          <div className="header-content">
            <div className="header-nav">
              {/* Range Toggle (Day/Week/Month/Custom) */}
              {!isMobile && (
                <div className="view-toggle">
                  <button
                    className={range === 'day' ? 'active' : ''}
                    onClick={this.handleRange}
                    value="day"
                  >
                    Day
                  </button>
                  <button
                    className={range === 'week' ? 'active' : ''}
                    onClick={this.handleRange}
                    value="week"
                  >
                    Week
                  </button>
                  <button
                    className={range === 'month' ? 'active' : ''}
                    onClick={this.handleRange}
                    value="month"
                  >
                    Month
                  </button>
                  <button
                    className={range === 'custom' ? 'active' : ''}
                    onClick={this.handleRange}
                    value="custom"
                  >
                    Custom
                  </button>
                </div>
              )}

              {/* Date Display Area */}
              <div className="date-display-area">
                {showCustomInputs ? (
                  <div className="custom-date-inputs-inline">
                    <div className="date-input-group">
                      <label htmlFor="customStartDate">From:</label>
                      <input
                        type="date"
                        id="customStartDate"
                        value={customStartDate}
                        onChange={this.handleCustomStartDateChange}
                        max={customEndDate || undefined}
                      />
                    </div>
                    <div className="date-input-group">
                      <label htmlFor="customEndDate">To:</label>
                      <input
                        type="date"
                        id="customEndDate"
                        value={customEndDate}
                        onChange={this.handleCustomEndDateChange}
                        min={customStartDate || undefined}
                      />
                    </div>
                    <button
                      className="apply-custom-date-btn"
                      onClick={this.handleCustomDateRange}
                      disabled={!customStartDate || !customEndDate}
                    >
                      Apply
                    </button>
                  </div>
                ) : (
                  <div className="date-display">
                    {this.getDisplayString(moment(selectedDate || today), range)}
                  </div>
                )}
              </div>

              {/* Navigation Controls */}
              <ButtonGroup className="nav-controls">
                <Button
                  variant="outline-secondary"
                  onClick={this.handleDisplayRange}
                  value="less"
                  disabled={range === 'custom'}
                >
                  ←
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={this.handleDisplayRange}
                  active={moment(selectedDate).isSame(today, 'day')}
                  value="today"
                  disabled={range === 'custom'}
                >
                  Today
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={this.handleDisplayRange}
                  value="more"
                  disabled={range === 'custom'}
                >
                  →
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </div>

        {/* Quick Actions & Filter */}
        <div className="action-buttons">
          <div className="action-row-left">
            <div className="label">Technician:</div>
            <select
              className="filter-select"
              value={selectedTechnician || ''}
              onChange={(e) => this.handleTechnicianChange(e.target.value)}
            >
              <option value="">All Technicians</option>
              {technicians.map(tech => (
                <option key={tech.technician_id} value={tech.technician_id}>
                  {tech.technician_name}
                </option>
              ))}
            </select>
          </div>
          <div className="action-row-right">
            <div className="label">Quick Actions:</div>
            <button
              className="action-btn download-btn"
              onClick={this.handleExport}
              disabled={exporting}
            >
              {exporting
                ? <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Exporting...</>
                : <><i className="fa-sharp fa-regular fa-file-csv mr-1"></i> Download CSV</>
              }
            </button>
          </div>
        </div>

        {/* Report Tabs */}
        <div className="text-center mb-3">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${activeTab === 'timesheet' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => this.setState({ activeTab: 'timesheet' })}
            >
              Timesheet
            </button>
            <button
              type="button"
              className={`btn ${activeTab === 'compliance' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => this.setState({ activeTab: 'compliance' })}
            >
              Compliance
            </button>
            <button
              type="button"
              className={`btn ${activeTab === 'exceptions' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => this.setState({ activeTab: 'exceptions' })}
            >
              Exceptions
            </button>
          </div>
        </div>

        <div>
          {activeTab === 'timesheet' && (
            <TimesheetReport
              locationId={effectiveLocationId}
              locations={this.props.locations}
              dateRange={dateRange}
              technicianId={selectedTechnician}
              onTechniciansLoaded={this.setTechnicians}
            />
          )}
          {activeTab === 'compliance' && (
            <ComplianceReport
              locationId={effectiveLocationId}
              locations={this.props.locations}
              dateRange={dateRange}
            />
          )}
          {activeTab === 'exceptions' && (
            <ExceptionReport
              locationId={effectiveLocationId}
              locations={this.props.locations}
              dateRange={dateRange}
            />
          )}
        </div>

        <style>{`
          .time-reports-container {
            padding: 24px;
            max-width: 1400px;
            margin: 0 auto;
            background: transparent;
            min-height: 100vh;
          }

          .time-reports-container.mobile {
            padding: 16px;
          }

          /* Header Section */
          .header-section {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 16px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }

          .header-content {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .header-nav {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            width: 100%;
            justify-content: space-between;
          }

          .mobile .header-nav {
            justify-content: space-between;
            flex-direction: column;
            gap: 12px;
          }

          /* View Toggle (Day/Week/Month/Custom) */
          .view-toggle {
            display: flex;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            overflow: hidden;
            flex-shrink: 0;
          }

          .view-toggle button {
            background: white;
            border: none;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 500;
            color: #6b7280;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .view-toggle button:hover:not(:disabled) {
            background: #f9fafb;
          }

          .view-toggle button.active {
            background: #3FB2BE;
            color: white;
          }

          .view-toggle button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          /* Date Display Area */
          .date-display-area {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 40px;
            gap: 4px;
          }

          .date-display {
            font-size: 16px;
            font-weight: 500;
            color: #374151;
            text-align: center;
          }

          .mobile .date-display {
            text-align: center;
            padding: 8px 0;
            order: -1;
          }

          /* Custom Date Inputs */
          .custom-date-inputs-inline {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px 16px;
            background: #f8fafc;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .date-input-group {
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .date-input-group label {
            font-size: 14px;
            font-weight: 500;
            color: #374151;
            white-space: nowrap;
          }

          .date-input-group input[type="date"] {
            padding: 6px 10px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            font-size: 14px;
          }

          .apply-custom-date-btn {
            background: #3FB2BE;
            color: white;
            border: 1px solid #3FB2BE;
            padding: 6px 16px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
          }

          .apply-custom-date-btn:hover:not(:disabled) {
            background: #359ca6;
            border-color: #359ca6;
          }

          .apply-custom-date-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .mobile .custom-date-inputs-inline {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: 100%;
          }

          .mobile .date-input-group {
            justify-content: space-between;
          }

          .mobile .date-input-group input[type="date"] {
            flex: 1;
            margin-left: 8px;
          }

          .nav-controls .btn {
            padding: 6px 12px;
          }

          /* Action Bar (filter + quick actions) */
          .action-buttons {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            flex-wrap: wrap;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }

          .action-row-left,
          .action-row-right {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .action-buttons .label {
            font-size: 14px;
            font-weight: 500;
            color: #374151;
            white-space: nowrap;
          }

          .filter-select {
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            transition: border-color 0.2s ease;
            background: white;
          }

          .filter-select:focus {
            outline: none;
            border-color: #3FB2BE;
            box-shadow: 0 0 0 2px rgba(63, 178, 190, 0.1);
          }

          .action-btn {
            background: white;
            border: 1px solid #e5e7eb;
            color: #6b7280;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .action-btn:hover:not(:disabled) {
            border-color: #d1d5db;
            background: #fafafa;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .action-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .download-btn {
            background: #3FB2BE !important;
            color: white !important;
            border-color: #3FB2BE !important;
          }

          .download-btn:hover:not(:disabled) {
            background: #359ca6 !important;
            border-color: #359ca6 !important;
            color: white !important;
          }

          /* Table Container */
          .table-container {
            padding: 20px;
          }

          .mobile .table-container {
            padding: 16px;
          }

          .table-container .table {
            margin: 0;
          }

          .table-container .table th {
            background: white;
            border-bottom: 2px solid #e5e7eb;
            color: #374151;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 12px 16px;
          }

          .table-container .table td {
            padding: 12px 16px;
            border-bottom: 1px solid #f3f4f6;
            vertical-align: middle;
          }

          .table-container .table tbody tr:hover {
            background: #f9fafb;
          }

          /* Overview Cards in child components */
          .overview-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 16px;
            margin-bottom: 24px;
          }

          .mobile .overview-cards {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 16px;
          }

          .overview-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px;
            text-align: center;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }

          .mobile .overview-card {
            padding: 16px 12px;
          }

          .overview-card .number {
            font-size: 20px;
            font-weight: 700;
            color: #374151;
            margin-bottom: 4px;
          }

          .mobile .overview-card .number {
            font-size: 16px;
          }

          .overview-card .label {
            font-size: 12px;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .overview-card.warning .number {
            color: #f59e0b;
          }

          .overview-card.danger .number {
            color: #ef4444;
          }

          .overview-card.success .number {
            color: #10b981;
          }
        `}</style>
      </div>
    );
  }
}

export default TimeReports;
